import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../components/auth'
import {
  COMPANY_LABEL,
  ROLE_LABEL,
  type UserCompany,
  type UserRole,
} from '../components/api'
import { Alert, Button, FormField, Input } from '../components/ui'

type DemoAccount = {
  email: string
  password: string
  name: string
  role: UserRole
  company: UserCompany
  note: string
}

const DEMO_PASSWORD = 'demo1234'

const DEMO_ACCOUNTS: DemoAccount[] = [
  {
    email: 'admin@demo.profe.jp',
    password: DEMO_PASSWORD,
    name: '管理者：山本敦',
    role: 'admin',
    company: 'improtec',
    note: '全権限・全顧客の FTP/DB 復号可',
  },
  {
    email: 'sato@demo.improtec.jp',
    password: DEMO_PASSWORD,
    name: 'IMPROTEC：佐藤健一',
    role: 'sales',
    company: 'improtec',
    note: '営業：顧客・契約管理',
  },
  {
    email: 'tanaka@demo.improtec.jp',
    password: DEMO_PASSWORD,
    name: 'IMPROTEC：田中美咲',
    role: 'sales',
    company: 'improtec',
    note: '営業：顧客・契約管理',
  },
  {
    email: 'suzuki@demo.feat.jp',
    password: DEMO_PASSWORD,
    name: 'FEAT.：鈴木浩二',
    role: 'dev',
    company: 'feat',
    note: '制作：自分担当顧客の FTP/DB 復号可',
  },
  {
    email: 'takahashi@demo.feat.jp',
    password: DEMO_PASSWORD,
    name: 'FEAT.：高橋翔',
    role: 'dev',
    company: 'feat',
    note: '制作：自分担当顧客の FTP/DB 復号可',
  },
]

export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setError(null)
    setSubmitting(true)
    try {
      await login(email.trim(), password)
      navigate('/', { replace: true })
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'ログインに失敗しました。時間をおいて再度お試しください。',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const fillDemo = (acc: DemoAccount) => {
    setEmail(acc.email)
    setPassword(acc.password)
    setError(null)
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <span className="auth-brand-mark">P</span>
          <span className="auth-brand-title">PROFE MAINT</span>
        </div>
        <p className="auth-sub">
          IMPROTEC＋FEAT. 統合保守管理ツール
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <form onSubmit={handleSubmit} noValidate>
          <FormField label="メールアドレス" htmlFor="email" required>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </FormField>
          <FormField label="パスワード" htmlFor="password" required>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </FormField>

          <Button
            type="submit"
            variant="primary"
            block
            disabled={submitting || !email || !password}
          >
            {submitting ? 'ログイン中…' : 'ログイン'}
          </Button>
        </form>

        <details className="accordion">
          <summary>デモアカウント一覧（pw = demo1234）</summary>
          <div className="accordion-body">
            <ul className="demo-list">
              {DEMO_ACCOUNTS.map((acc) => (
                <li key={acc.email} className="demo-item">
                  <div className="demo-meta">
                    <span className="demo-email">{acc.email}</span>
                    <span className="demo-role">
                      {COMPANY_LABEL[acc.company]} ／ {ROLE_LABEL[acc.role]} ／{' '}
                      {acc.note}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => fillDemo(acc)}
                  >
                    自動入力
                  </Button>
                </li>
              ))}
            </ul>
            <p className="hint" style={{ marginTop: 8 }}>
              ※ 本番運用時は初回ログイン後にパスワードを変更してください。
            </p>
          </div>
        </details>
      </div>
    </div>
  )
}
